/* eslint-disable*/
import React, { useEffect, useState } from 'react';

import Layout from 'antd/lib/layout';

import { Map, Marker, NavigationControl } from '@vis.gl/react-maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import { MultiBuildingModal } from '../MultiBuildingModal';
import { AddBuildingModal } from './components/AddBuildingModal';

import './MapView.less';

import mapMarker from './assets/map-marker.svg';
import mapMarkerRed from './assets/map-marker-red.svg';
import mapMarkerHL from './assets/map-marker-hl.svg';
import mapMarkerDC from './assets/map-marker-dc.svg';

// MapLibre uses 512px tiles, so zoom levels are one lower than Google's for the same scale.
const MAX_AUTO_ZOOM = 14;
const DEFAULT_ZOOM = 12;
const FIT_BOUNDS_PADDING = 64;
const BUILDING_CARD_WIDTH = 670;
const CARD_FULLSCREEN_BREAKPOINT = 768;

// TODO: temporary hardcoded fallback. Move the key to CI env vars (REACT_APP_AWS_MAPS_API_KEY)
// and rotate it. Amazon Location keys are public by design, but this one should be referer-restricted.
const AWS_MAPS_API_KEY = process.env.REACT_APP_AWS_MAPS_API_KEY || 'v1.public.eyJqdGkiOiI4NDFmZGNhMy01ZjkyLTRhYmMtOTVhYi0xMGVjZjcwNmY0ZTkifYkt8oQ4HAUMwp0vLzwgv9x-6ATmpiG9yB5KGQ0ouvsZ68iNvyt5PYcjmyfFQ6w4mI1vU1K5PB9zxLdVjY1UM6j9AlscNTZhSKzTM1SQxTLI4orikaAOO3bC9Wdi1AE2Jq0j71HCkeP0MGcYp_4CgpQGhYytDctMMR7gQsmhVWBtBhu_5XAgQo0-jWWLYedMRNZqapunZOkGj_9jwC5tjbWqdapotNu9h_qkRNJeiZr64_LeKyG1XTErvhqf7plsQl-wjcBnvYpoWy1Yba15ukkt1hVCQdwarDZWqom8aiyLRIVj-iblBzCyxudHCnG1uWes5VN_ECWzYyP39jrT_fE.ZWU0ZWIzMTktMWRhNi00Mzg0LTllMzYtNzlmMDU3MjRmYTkx';
const AWS_REGION = process.env.REACT_APP_AWS_REGION || 'us-east-1';

export function getMapStyleUrl(theme) {
  const colorScheme = theme === 'light' ? 'Light' : 'Dark';
  return `https://maps.geo.${AWS_REGION}.amazonaws.com/v2/styles/Standard/descriptor?key=${AWS_MAPS_API_KEY}&color-scheme=${colorScheme}`;
}

// Hide shops/restaurants/etc. so building markers stand out (the old Google style did the same).
// Runs on every style load, including theme switches. Guarded so it doesn't retrigger itself.
export function hidePointsOfInterest(map) {
  map.getStyle()?.layers?.forEach(layer => {
    if (layer['source-layer'] === 'pois' && map.getLayoutProperty(layer.id, 'visibility') !== 'none') {
      map.setLayoutProperty(layer.id, 'visibility', 'none');
    }
  });
}

function getPosition(building) {
  return {
    lat: parseFloat(building.latitude),
    lng: parseFloat(building.longitude)
  }
}

function getCardCenterOffsetLng(map) {
  const zoom = map.getZoom();
  if (typeof zoom !== 'number') return 0;

  const mapWidth = map.getContainer()?.offsetWidth ?? window.innerWidth;
  if (mapWidth <= CARD_FULLSCREEN_BREAKPOINT) return 0;

  const degreesPerPixel = 360 / (512 * Math.pow(2, zoom));
  return (BUILDING_CARD_WIDTH / 2) * degreesPerPixel;
}

function getMarkerIcon(building, selectedBuildingId) {
  if (!building.is_connected) return mapMarkerDC;
  if (selectedBuildingId === building.identifier) return mapMarkerHL;
  if (building.alert_count) return mapMarkerRed;
  return mapMarker;
}

export const BuildingMarkers = ({ buildings, selectedBuildingId, onSelectBuilding }) => {
  return (
    <>
      {buildings?.map(building => {
        const { lat, lng } = getPosition(building);
        return (
          <Marker
            key={building.identifier}
            longitude={lng}
            latitude={lat}
            anchor="bottom"
            style={{ zIndex: selectedBuildingId === building.identifier ? 10 : 1, cursor: 'pointer' }}
            onClick={(e) => {
              e.originalEvent.stopPropagation();
              onSelectBuilding(building);
            }}
          >
            <img data-testid={building.identifier} src={getMarkerIcon(building, selectedBuildingId)} alt="" />
          </Marker>
        );
      })}
    </>
  );
};

const MapView = (props) => {
  const {
    theme,
    isAddBuildingModalOpen,
    setIsAddBuildingModalOpen,
    selectedBuildingId,
    selectBuilding,
    selectedBuildingInfo,
    buildings,
  } = props;

  const [mapReference, setMapReference] = useState(null);

  useEffect(() => {
    window.document.title = `Home`;

    sessionStorage.removeItem('selectedBuilding');

  }, [isAddBuildingModalOpen]);

  const closeAddBuildingModal = async () => {
    setIsAddBuildingModalOpen(false);
  };

  useEffect(() => {
    if (!mapReference) return;
    if (!selectedBuildingInfo?.latitude) return;

    const { lat, lng } = getPosition(selectedBuildingInfo);
    mapReference.setCenter([lng + getCardCenterOffsetLng(mapReference), lat]);
  }, [selectedBuildingInfo, mapReference])

  useEffect(() => {
    if (!mapReference) return;
    if (!buildings?.length) return;

    if (buildings.length === 1) {
      const { lat, lng } = getPosition(buildings[0]);
      mapReference.setCenter([lng, lat]);
      mapReference.setZoom(MAX_AUTO_ZOOM);
      return;
    }

    const positions = buildings.map(getPosition);
    const lngs = positions.map(p => p.lng);
    const lats = positions.map(p => p.lat);
    mapReference.fitBounds(
      [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
      { padding: FIT_BOUNDS_PADDING, maxZoom: MAX_AUTO_ZOOM }
    );
  }, [buildings, mapReference])

  return (
    <Layout className="mbh-wrapper">
      {isAddBuildingModalOpen && <AddBuildingModal onCancel={closeAddBuildingModal} theme={theme} />}
      <div className="main-map" data-testid="map">
        <Map style={{ width: '100%', height: '100%' }}
          mapStyle={getMapStyleUrl(theme)}
          initialViewState={{ latitude: 43.6464503, longitude: -79.4049989, zoom: DEFAULT_ZOOM }}
          onLoad={(e) => setMapReference(e.target)}
          onStyleData={(e) => hidePointsOfInterest(e.target)}
        >
          <NavigationControl position="top-right" showCompass={false} />
          <BuildingMarkers buildings={buildings} selectedBuildingId={selectedBuildingId} onSelectBuilding={selectBuilding} />
        </Map>
      </div>
      {selectedBuildingId && (
        <MultiBuildingModal
          buildingInfo={selectedBuildingInfo}
          buildingId={selectedBuildingId}
          onCancel={() => {
            selectBuilding({});
          }}
          theme={theme}
          buildings={buildings}
        />
      )}
    </Layout>
  );
};

export default MapView;
